import React, { useContext, useRef, useEffect } from "react";
import {
  Form,
  Input,
  InputNumber,
  Slider,
  Select,
  Checkbox,
  Row,
  Button,
  Collapse,
  Tag,
} from "antd";
import type { InputRef, InputNumberProps } from "antd";
import {
  EyeInvisibleOutlined,
  EyeTwoTone,
  PlusOutlined,
} from "@ant-design/icons";
import { DatePicker, TimePicker } from "../antOverrides";

import moment, { Moment } from "moment";
import { getFormItemLabel, getFormItemVaidation } from "../util/helpers";
import PropTypes from "prop-types";
import { useIntl, FormattedMessage } from "react-intl";
import RestrictedContext from "../util/RestrictedContext";

const { TextArea } = Input;
const { Option } = Select;
const { Panel } = Collapse;
const { RangePicker } = DatePicker;

interface InputItemProps {
  name: string | string[];
  required?: boolean;
  number?: boolean;
  email?: boolean;
  password?: boolean;
  id?: boolean;
  placeholder?: string;
  min?: number;
  max?: number;
  postalCode?: boolean;
  onlyNumbers?: boolean;
  onlyDigits?: boolean;
  oneTo999Numbers?: boolean;
  oneToNineNumbers?: boolean;
  oneToNinetyNineNumbers?: boolean;
  zeroToNinetyNineNumbers?: boolean;
  onlyPositiveNumbers?: boolean;
  twoDigitsNumbers?: boolean;
  fivePositiveNumbers?: boolean;
  dayInMonth?: boolean;
  eightPositiveNumbers?: boolean;
  ninePositiveNumbers?: boolean;
  fifteenCharacters?: boolean;
  domainOrIp?: boolean;
  passport?: boolean;
  url?: boolean;
  english?: boolean;
  category?: string;
  disabled?: boolean;
  onChange?: (value: any) => void;
  itemLabel?: string;
  preserve?: boolean;
  step?: number;
  autoComplete?: string;
  precision?: number;
  allowClear?: boolean;
  direction?: string;
  autoFocus?: boolean;
  style?: React.CSSProperties;
  inputStyle?: React.CSSProperties;
  hideLabel?: boolean;
  prefix?: React.ReactNode;
  size?: "large" | "middle" | "small";
  dynamicFieldName?: string;
}

const InputItem: React.FC<InputItemProps> = (props) => {
  const {
    name,
    required,
    number,
    email,
    password,
    id,
    placeholder,
    min,
    max,
    postalCode,
    onlyNumbers,
    onlyDigits,
    oneTo999Numbers,
    oneToNineNumbers,
    oneToNinetyNineNumbers,
    zeroToNinetyNineNumbers,
    onlyPositiveNumbers,
    fivePositiveNumbers,
    eightPositiveNumbers,
    ninePositiveNumbers,
    dayInMonth,
    twoDigitsNumbers,
    fifteenCharacters,
    domainOrIp,
    passport,
    url,
    english,
    category,
    disabled,
    onChange,
    itemLabel,
    preserve,
    step,
    autoComplete,
    precision,
    allowClear,
    direction,
    autoFocus,
    style,
    inputStyle,
    hideLabel,
    prefix,
    size,
  } = props;
  const restricted = useContext(RestrictedContext);
  const intl = useIntl();
  const label = itemLabel
    ? getFormItemLabel(intl, category, itemLabel, null)
    : getFormItemLabel(intl, category, name, null);
  const inputRef = useRef<any>(null);

  useEffect(() => {
    if (autoFocus && inputRef && inputRef.current && !number) {
      const { input } = inputRef.current as unknown as {
        input: HTMLInputElement;
      };
      input?.focus();
    }
  }, [autoFocus, number]);

  const arr: string[] = [];
  if (required) {
    arr.push("required");
  }
  if (email) {
    arr.push("email");
  }
  if (id) {
    arr.push("id");
  }
  if (postalCode) {
    arr.push("postalCode");
  }
  if (english) {
    arr.push("english");
  }
  if (passport) {
    arr.push("passport");
  }
  if (onlyNumbers) {
    arr.push("onlyNumbers");
  }
  if (onlyDigits) {
    arr.push("onlyDigits");
  }
  if (oneTo999Numbers) {
    arr.push("oneTo999Numbers");
  }
  if (oneToNineNumbers) {
    arr.push("oneToNineNumbers");
  }
  if (oneToNinetyNineNumbers) {
    arr.push("oneToNinetyNineNumbers");
  }
  if (zeroToNinetyNineNumbers) {
    arr.push("zeroToNinetyNineNumbers");
  }
  if (onlyPositiveNumbers) {
    arr.push("onlyPositiveNumbers");
  }
  if (twoDigitsNumbers) {
    arr.push("twoDigitsNumbers");
  }
  if (fifteenCharacters) {
    arr.push("fifteenCharacters");
  }
  if (domainOrIp) {
    arr.push("domainOrIp");
  }
  if (url) {
    arr.push("url");
  }

  if (fivePositiveNumbers) {
    arr.push("fivePositiveNumbers");
  }
  if (eightPositiveNumbers) {
    arr.push("eightPositiveNumbers");
  }
  if (ninePositiveNumbers) {
    arr.push("ninePositiveNumbers");
  }
  if (dayInMonth) {
    arr.push("dayInMonth");
  }

  const rules = getFormItemVaidation(intl, label, arr);

  return (
    <Form.Item
      preserve={preserve}
      label={hideLabel ? null : label}
      name={name}
      rules={rules}
      normalize={(value, prevValue, prevValues) =>
        email && value === ""
          ? null
          : passport && value
          ? value.toUpperCase()
          : value
      }
      style={style}
    >
      {number ? (
        <InputNumber
          min={min}
          max={max}
          placeholder={placeholder}
          step={step}
          style={inputStyle ? inputStyle : { width: "100%" }}
          disabled={disabled || !restricted["hasWritePermission"]}
          ref={inputRef}
          autoFocus={autoFocus}
        />
      ) : password ? (
        <Input.Password
          disabled={disabled || !restricted["hasWritePermission"]}
          iconRender={(visible) =>
            visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
          }
          ref={inputRef}
          prefix={prefix}
          placeholder={placeholder}
        />
      ) : (
        <Input
          disabled={disabled || !restricted["hasWritePermission"]}
          autoComplete={autoComplete ? autoComplete : "off"}
          allowClear={allowClear}
          style={
            inputStyle
              ? inputStyle
              : direction
              ? { direction: direction as "ltr" | "rtl" }
              : undefined
          }
          ref={inputRef}
          prefix={prefix}
          placeholder={placeholder}
          size={size}
        />
      )}
    </Form.Item>
  );
};

interface TextAreaItemProps {
  name: string;
  required?: boolean;
  rows?: number;
  category?: string;
  disabled?: boolean;
}

const TextAreaItem: React.FC<TextAreaItemProps> = (props) => {
  const { name, required, rows, category, disabled } = props;
  const intl = useIntl();
  const label = getFormItemLabel(intl, category, name, null);
  const arr: string[] = [];
  if (required) {
    arr.push("required");
  }
  const rules = getFormItemVaidation(intl, label, arr);
  const restricted = useContext(RestrictedContext);
  return (
    <Form.Item label={label} name={name} rules={rules}>
      <TextArea
        rows={rows}
        disabled={!restricted["hasWritePermission"] || disabled}
      />
    </Form.Item>
  );
};

interface SliderItemProps {
  name: string;
  required?: boolean;
  category?: string;
}

const SliderItem: React.FC<SliderItemProps> = (props) => {
  const { name, required, category } = props;
  const intl = useIntl();
  const label = getFormItemLabel(intl, category, name, null);
  const arr: string[] = [];
  if (required) {
    arr.push("required");
  }
  const rules = getFormItemVaidation(intl, label, arr);
  const restricted = useContext(RestrictedContext);
  return (
    <Form.Item label={label} name={name} rules={rules}>
      <Slider disabled={!restricted["hasWritePermission"]} />
    </Form.Item>
  );
};

interface SelectItemProps {
  name: string | string[];
  required?: boolean;
  category?: string;
  data?: { value: any; name: React.ReactNode }[];
  yesNo?: boolean;
  marital?: boolean;
  gender?: boolean;
  employeeType?: boolean;
  religion?: boolean;
  role?: boolean;
  status?: boolean;
  days?: boolean;
  daysInt?: boolean;
  organizationType?: boolean;
  clientsType?: boolean;
  taskType?: boolean;
  duration?: boolean;
  contractDayType?: boolean;
  crmTaskPriority?: boolean;
  crmTaskStatus?: boolean;
  controller?: boolean;
  missingHoursReason?: boolean;
  transportation?: boolean;
  reasonNotApproved?: boolean;
  diariesExportType?: boolean;
  benefitLevels?: boolean;
  houseVisitsMonths?: boolean;
  otherIncomeSource?: boolean;
  employeeOtherIncomeSource?: boolean;
  dynamicFieldName?: string;
  mode?: "multiple" | "tags";
  disabled?: boolean;
  filterItems?: (
    data: { value: any; name: React.ReactNode }[]
  ) => { value: any; name: React.ReactNode }[];
  onChange?: (value: any) => void;
  [key: string]: any;
}

const SelectItem: React.FC<SelectItemProps> = (props) => {
  const {
    name,
    required,
    category,
    data,
    yesNo,
    marital,
    gender,
    employeeType,
    religion,
    role,
    status,
    days,
    daysInt,
    organizationType,
    clientsType,
    taskType,
    duration,
    contractDayType,
    crmTaskPriority,
    crmTaskStatus,
    controller,
    missingHoursReason,
    transportation,
    reasonNotApproved,
    diariesExportType,
    benefitLevels,
    houseVisitsMonths,
    otherIncomeSource,
    employeeOtherIncomeSource,
    dynamicFieldName,
    mode,
    disabled,
    filterItems,
    onChange,
    ...rest
  } = props;
  const intl = useIntl();
  const label = getFormItemLabel(intl, category, name, dynamicFieldName);
  const arr: string[] = [];
  if (required) {
    arr.push("required");
  }
  const rules = getFormItemVaidation(intl, label, arr);
  const yesNoData = [
    { value: true, name: <FormattedMessage id="app.forms.yes" /> },
    { value: false, name: <FormattedMessage id="app.forms.no" /> },
  ];

  const maritalData = [
    { value: 1, name: <FormattedMessage id="app.forms.married" /> },
    { value: 2, name: <FormattedMessage id="app.forms.divorced" /> },
    { value: 3, name: <FormattedMessage id="app.forms.bachelor" /> },
    { value: 4, name: <FormattedMessage id="app.forms.widow" /> },
  ];

  const genderData = [
    { value: 1, name: <FormattedMessage id="app.forms.male" /> },
    { value: 2, name: <FormattedMessage id="app.forms.female" /> },
  ];

  const employeeTypeData = [
    { value: 1, name: <FormattedMessage id="app.forms.israeli" /> },
    { value: 2, name: <FormattedMessage id="app.forms.foreign" /> },
    {
      value: 3,
      name: <FormattedMessage id="app.forms.palestinianWorker" />,
    },
  ];

  const statusData = [
    { value: 1, name: <FormattedMessage id="app.forms.active" /> },
    {
      value: 2,
      name: <FormattedMessage id="app.forms.not_active" />,
    },
    { value: 3, name: <FormattedMessage id="app.forms.candidate" /> },
  ];

  const religionData = [
    { value: "christian", name: <FormattedMessage id="app.forms.christian" /> },
    { value: "moslem", name: <FormattedMessage id="app.forms.moslem" /> },
    { value: "jewish", name: <FormattedMessage id="app.forms.jewish" /> },
  ];

  const daysData = [
    { value: "1", name: <FormattedMessage id="app.forms.sunday" /> },
    { value: "2", name: <FormattedMessage id="app.forms.monday" /> },
    { value: "3", name: <FormattedMessage id="app.forms.tuesday" /> },
    { value: "4", name: <FormattedMessage id="app.forms.wednesday" /> },
    { value: "5", name: <FormattedMessage id="app.forms.thursday" /> },
    { value: "6", name: <FormattedMessage id="app.forms.friday" /> },
    { value: "7", name: <FormattedMessage id="app.forms.satuarday" /> },
  ];

  const daysIntData = [
    { value: 0, name: <FormattedMessage id="app.forms.sunday" /> },
    { value: 1, name: <FormattedMessage id="app.forms.monday" /> },
    { value: 2, name: <FormattedMessage id="app.forms.tuesday" /> },
    { value: 3, name: <FormattedMessage id="app.forms.wednesday" /> },
    { value: 4, name: <FormattedMessage id="app.forms.thursday" /> },
    { value: 5, name: <FormattedMessage id="app.forms.friday" /> },
    { value: 6, name: <FormattedMessage id="app.forms.satuarday" /> },
  ];

  const contractDayTypeData = [
    { value: 1, name: <FormattedMessage id="app.forms.weekDay" /> },
    { value: 2, name: <FormattedMessage id="app.forms.holiday" /> },
    { value: 3, name: <FormattedMessage id="app.forms.holidayNight" /> },
  ];

  const roleData = [
    { value: 1, name: <FormattedMessage id="app.forms.reviewer" /> },
    { value: 2, name: <FormattedMessage id="app.forms.marketer" /> },
    { value: 3, name: <FormattedMessage id="app.forms.coordinator" /> },
    { value: 4, name: <FormattedMessage id="app.forms.accountant" /> },
    {
      value: 5,
      name: <FormattedMessage id="app.forms.professional_employee" />,
    },
    {
      value: 6,
      name: <FormattedMessage id="app.forms.team_leader" />,
    },
    { value: 7, name: <FormattedMessage id="app.forms.branch_manager" /> },
    { value: 8, name: <FormattedMessage id="app.forms.regional_manager" /> },
    {
      value: 9,
      name: <FormattedMessage id="app.forms.organization_manager" />,
    },
  ];

  const clientsTypeData = [
    {
      value: 1,
      name: <FormattedMessage id="app.dataSelect.clients_type.btl" />,
    },
    {
      value: 2,
      name: <FormattedMessage id="app.dataSelect.clients_type.survivor_fund" />,
    },
    {
      value: 3,
      name: <FormattedMessage id="app.dataSelect.clients_type.day_centers" />,
    },
    {
      value: 4,
      name: (
        <FormattedMessage id="app.dataSelect.clients_type.ministry_of_defence" />
      ),
    },
    {
      value: 5,
      name: <FormattedMessage id="app.dataSelect.clients_type.private" />,
    },
  ];
  const organizationTypeData = [
    { value: 1, name: <FormattedMessage id="app.forms.company" /> },
    { value: 2, name: <FormattedMessage id="app.forms.nonProfit" /> },
  ];
  const taskData = [
    {
      value: "opened",
      name: <FormattedMessage id="app.dataSelect.taskType.opened" />,
    },
    {
      value: "inprogress",
      name: <FormattedMessage id="app.dataSelect.taskType.inprogress" />,
    },
    {
      value: "cancelled",
      name: <FormattedMessage id="app.dataSelect.taskType.cancelled" />,
    },
    {
      value: "completed",
      name: <FormattedMessage id="app.dataSelect.taskType.completed" />,
    },
  ];
  const controllerData = [
    { value: null, name: <FormattedMessage id="app.forms.unProfessional" /> },
    { value: 3, name: <FormattedMessage id="app.forms.controller" /> },
  ];

  const reasonNotApprovedData = [
    {
      value: 1,
      name: <FormattedMessage id="app.dataSelect.houseVisit.hospitalization" />,
    },
    {
      value: 2,
      name: <FormattedMessage id="app.dataSelect.houseVisit.absence" />,
    },
    {
      value: 3,
      name: <FormattedMessage id="app.dataSelect.houseVisit.dayCenter" />,
    },
    {
      value: 4,
      name: (
        <FormattedMessage id="app.dataSelect.houseVisit.householdMemberAbsence" />
      ),
    },
    {
      value: 5,
      name: <FormattedMessage id="app.dataSelect.houseVisit.refusaToOpen" />,
    },
    {
      value: 6,
      name: (
        <FormattedMessage id="app.dataSelect.houseVisit.refuseToCooperate" />
      ),
    },
    {
      value: 7,
      name: (
        <FormattedMessage id="app.dataSelect.houseVisit.InaccessiblePlace" />
      ),
    },
    {
      value: 8,
      name: <FormattedMessage id="app.dataSelect.houseVisit.otherReason" />,
    },
    {
      value: 9,
      name: (
        <FormattedMessage id="app.dataSelect.houseVisit.noObligationToVisit" />
      ),
    },
    {
      value: 10,
      name: (
        <FormattedMessage id="app.dataSelect.houseVisit.completionOfHours" />
      ),
    },
  ];

  const missingHoursReasonData = [
    {
      value: 1,
      name: (
        <FormattedMessage id="app.dataSelect.missingHoursReason.startMiddleOfMonth" />
      ),
    },
    {
      value: 2,
      name: (
        <FormattedMessage id="app.dataSelect.missingHoursReason.endMiddleOfMonth" />
      ),
    },
    {
      value: 3,
      name: <FormattedMessage id="app.dataSelect.missingHoursReason.death" />,
    },
    {
      value: 4,
      name: (
        <FormattedMessage id="app.dataSelect.missingHoursReason.hospitalization" />
      ),
    },
    {
      value: 5,
      name: <FormattedMessage id="app.dataSelect.missingHoursReason.absence" />,
    },
    {
      value: 6,
      name: <FormattedMessage id="app.dataSelect.missingHoursReason.other" />,
    },
    {
      value: 9,
      name: (
        <FormattedMessage id="app.dataSelect.missingHoursReason.holidayTariff" />
      ),
    },
    {
      value: 0,
      name: (
        <FormattedMessage id="app.dataSelect.missingHoursReason.noReason" />
      ),
    },
  ];

  const durationData = [
    {
      value: 15,
      name: <FormattedMessage id="app.dataSelect.duration.15minutes" />,
    },
    {
      value: 20,
      name: <FormattedMessage id="app.dataSelect.duration.20minutes" />,
    },
    {
      value: 25,
      name: <FormattedMessage id="app.dataSelect.duration.25minutes" />,
    },
    {
      value: 30,
      name: <FormattedMessage id="app.dataSelect.duration.30minutes" />,
    },
    {
      value: 35,
      name: <FormattedMessage id="app.dataSelect.duration.35minutes" />,
    },
    {
      value: 40,
      name: <FormattedMessage id="app.dataSelect.duration.40minutes" />,
    },
    {
      value: 45,
      name: <FormattedMessage id="app.dataSelect.duration.45minutes" />,
    },
    {
      value: 50,
      name: <FormattedMessage id="app.dataSelect.duration.50minutes" />,
    },
    {
      value: 55,
      name: <FormattedMessage id="app.dataSelect.duration.55minutes" />,
    },
    {
      value: 60,
      name: <FormattedMessage id="app.dataSelect.duration.60minutes" />,
    },
  ];

  const transportationStrategyData = [
    {
      value: "regular",
      name: <FormattedMessage id="app.forms.employee.regular_transportation" />,
    },
    {
      value: "enforce_daily",
      name: (
        <FormattedMessage id="app.forms.employee.enforce_daily_transportation" />
      ),
    },
    {
      value: "enforce_monthly",
      name: (
        <FormattedMessage id="app.forms.employee.enforce_monthly_transportation" />
      ),
    },
  ];
  const diariesExportTypeData = [
    {
      value: "all",
      name: <FormattedMessage id="app.forms.employee.all" />,
    },
    {
      value: "staff_worker_in_charge",
      name: <FormattedMessage id="app.forms.employee.staff_worker_in_charge" />,
    },
  ];

  const crmTaskPriorityData = [
    {
      value: "none",
      name: <FormattedMessage id="app.forms.todolist.priority_none" />,
    },
    {
      value: "high",
      name: <FormattedMessage id="app.forms.todolist.priority_high" />,
    },
    {
      value: "normal",
      name: <FormattedMessage id="app.forms.todolist.priority_normal" />,
    },
  ];

  const crmTaskStatusData = [
    {
      value: "none",
      name: <FormattedMessage id="app.forms.todolist.status_none" />,
    },
    {
      value: "open",
      name: <FormattedMessage id="app.forms.todolist.status_open" />,
    },
  ];

  const benefitLevelsData = [
    {
      value: 1,
      name: <FormattedMessage id="app.dataSelect.benefit_level_1" />,
    },
    {
      value: 2,
      name: <FormattedMessage id="app.dataSelect.benefit_level_2" />,
    },
    {
      value: 3,
      name: <FormattedMessage id="app.dataSelect.benefit_level_3" />,
    },
    {
      value: 4,
      name: <FormattedMessage id="app.dataSelect.benefit_level_4" />,
    },
    {
      value: 5,
      name: <FormattedMessage id="app.dataSelect.benefit_level_5" />,
    },
    {
      value: 6,
      name: <FormattedMessage id="app.dataSelect.benefit_level_6" />,
    },
  ];

  const otherIncomeSourceData = [
    {
      value: 1,
      name: <FormattedMessage id="app.dataSelect.otherIncomeSource_work" />,
    },
    {
      value: 2,
      name: <FormattedMessage id="app.dataSelect.otherIncomeSource_other" />,
    },
  ];

  const employeeOtherIncomeSourceData = [
    {
      value: 1,
      name: (
        <FormattedMessage id="app.dataSelect.employeeOtherIncomeSource_1" />
      ),
    },
    {
      value: 2,
      name: (
        <FormattedMessage id="app.dataSelect.employeeOtherIncomeSource_2" />
      ),
    },
    {
      value: 3,
      name: (
        <FormattedMessage id="app.dataSelect.employeeOtherIncomeSource_3" />
      ),
    },
    {
      value: 4,
      name: (
        <FormattedMessage id="app.dataSelect.employeeOtherIncomeSource_4" />
      ),
    },
    {
      value: 5,
      name: (
        <FormattedMessage id="app.dataSelect.employeeOtherIncomeSource_5" />
      ),
    },
    {
      value: 6,
      name: (
        <FormattedMessage id="app.dataSelect.employeeOtherIncomeSource_6" />
      ),
    },
  ];

  const houseVisitsMonthsData: { value: number; name: string }[] = [];

  for (let i = 1; i <= 24; i++) {
    houseVisitsMonthsData.push({
      value: i,
      name: String(i),
    });
  }

  let selectData = data || [];
  if (yesNo) {
    selectData = yesNoData;
  } else if (marital) {
    selectData = maritalData;
  } else if (gender) {
    selectData = genderData;
  } else if (employeeType) {
    selectData = employeeTypeData;
  } else if (religion) {
    selectData = religionData;
  } else if (status) {
    selectData = statusData;
  } else if (days) {
    selectData = daysData;
  } else if (daysInt) {
    selectData = daysIntData;
  } else if (role) {
    selectData = roleData;
  } else if (clientsType) {
    selectData = clientsTypeData;
  } else if (organizationType) {
    selectData = organizationTypeData;
  } else if (taskType) {
    selectData = taskData;
  } else if (controller) {
    selectData = controllerData;
  } else if (reasonNotApproved) {
    selectData = reasonNotApprovedData;
  } else if (missingHoursReason) {
    selectData = missingHoursReasonData;
  } else if (duration) {
    selectData = durationData;
  } else if (contractDayType) {
    selectData = contractDayTypeData;
  } else if (transportation) {
    selectData = transportationStrategyData;
  } else if (diariesExportType) {
    selectData = diariesExportTypeData;
  } else if (crmTaskPriority) {
    selectData = crmTaskPriorityData;
  } else if (crmTaskStatus) {
    selectData = crmTaskStatusData;
  } else if (benefitLevels) {
    selectData = benefitLevelsData;
  } else if (houseVisitsMonths) {
    selectData = houseVisitsMonthsData;
  } else if (otherIncomeSource) {
    selectData = otherIncomeSourceData;
  } else if (employeeOtherIncomeSource) {
    selectData = employeeOtherIncomeSourceData;
  }

  const filtered = filterItems ? filterItems(selectData) : selectData;

  const tagRender = (props: {
    label: React.ReactNode;
    closable: boolean;
    onClose: () => void;
  }) => {
    const { label, closable, onClose } = props;
    return (
      <Tag closable={closable} onClose={onClose} style={{ marginRight: 3 }}>
        {label}
      </Tag>
    );
  };
  const restricted = useContext(RestrictedContext);
  return (
    <Form.Item label={label} name={name} rules={rules}>
      <Select
        tagRender={tagRender}
        mode={mode}
        onChange={onChange}
        disabled={disabled || !restricted["hasWritePermission"]}
        {...rest}
      >
        {filtered.map((option) => (
          <Option key={option.value} value={option.value}>
            {option.name}
          </Option>
        ))}
      </Select>
    </Form.Item>
  );
};

interface DatePickerItemProps {
  name: string | string[];
  dynamicFieldName?: string;
  required?: boolean;
  category?: string;
  disabled?: boolean;
  hideLabel?: boolean;
  disabledDate?: (current: Moment) => boolean;
  defaultPickerValue?: Moment;
  format?: string;
  picker?: "date" | "week" | "month" | "quarter" | "year";
  formRules?: any[];
  style?: React.CSSProperties;
}

const DatePickerItem: React.FC<DatePickerItemProps> = (props) => {
  const DatePickerComponent: React.FC<{
    value?: string;
    onChange: (value: string | null) => void;
    disabled?: boolean;
    disabledDate?: (current: Moment) => boolean;
    defaultPickerValue?: Moment;
    picker?: "date" | "week" | "month" | "quarter" | "year";
  }> = ({
    value,
    onChange,
    disabled,
    disabledDate,
    defaultPickerValue,
    picker,
  }) => {
    const onDateChange = (val: Moment | null) => {
      if (val) {
        onChange(moment(val).toISOString());
      } else {
        onChange(null);
      }
    };
    const restricted = useContext(RestrictedContext);
    return (
      <DatePicker
        disabled={disabled || !restricted["hasWritePermission"]}
        value={value ? moment(value) : null}
        format={dateFormat}
        picker={picker}
        style={{ width: "100%" }}
        onChange={onDateChange}
        disabledDate={disabledDate}
        defaultValue={
          value && moment(value).isValid() ? moment(value) : defaultPickerValue
        }
      />
    );
  };
  const {
    name,
    required,
    category,
    dynamicFieldName,
    disabled,
    hideLabel,
    disabledDate,
    defaultPickerValue,
    format,
    picker,
    formRules,
    style,
  } = props;
  const intl = useIntl();
  const label = hideLabel
    ? null
    : getFormItemLabel(intl, category, name, dynamicFieldName);
  const arr: string[] = [];
  if (required) {
    arr.push("required");
  }
  const rules = getFormItemVaidation(intl, label, arr);
  const dateFormat = format ? format : "DD/MM/YYYY";
  const restricted = useContext(RestrictedContext);
  return (
    <Form.Item
      label={hideLabel ? null : label}
      name={name}
      rules={formRules ? [...formRules, rules] : rules}
      style={style}
    >
      <DatePickerComponent
        disabled={disabled || !restricted["hasWritePermission"]}
        disabledDate={disabledDate}
        defaultPickerValue={defaultPickerValue}
        picker={picker}
        onChange={() => {}}
      />
    </Form.Item>
  );
};

interface HourPickerItemProps {
  name: string | string[];
  dynamicFieldName?: string;
  required?: boolean;
  category?: string;
  disabled?: boolean;
  hideLabel?: boolean;
  disabledHours?: () => number[];
  disabledMinutes?: (selectedHour: number) => number[];
  defaultValue?: Moment;
  format?: string;
  formRules?: any[];
  onOpenChange?: (open: boolean) => void;
}

const HourPickerItem: React.FC<HourPickerItemProps> = (props) => {
  const HourPickerComponent: React.FC<{
    value?: string;
    onChange: (value: string) => void;
    disabled?: boolean;
    disabledHours?: () => number[];
    disabledMinutes?: (selectedHour: number) => number[];
    defaultValue?: Moment;
    onOpenChange?: (open: boolean) => void;
  }> = ({
    value,
    onChange,
    disabled,
    disabledHours,
    disabledMinutes,
    defaultValue,
    onOpenChange,
  }) => {
    const onDateChange = (val: Moment | null) => {
      if (val) {
        onChange(moment(val).toISOString());
      }
    };
    const restricted = useContext(RestrictedContext);

    const disabledTime = () => ({
      disabledHours: () => (disabledHours ? disabledHours() : []),
      disabledMinutes: (selectedHour: number) =>
        disabledMinutes ? disabledMinutes(selectedHour) : [],
    });

    const timePickerProps: any = {
      value: value ? moment(value) : undefined,
      format: dateFormat,
      style: { width: "100%" },
      onChange: onDateChange,
      minuteStep: 15,
      onOpenChange: onOpenChange,
    };

    if (disabled || !restricted["hasWritePermission"]) {
      timePickerProps.disabled = true;
    }

    if (disabledHours) {
      timePickerProps.disabledTime = disabledTime;
    }

    if (defaultValue) {
      timePickerProps.defaultValue = defaultValue;
    }

    return <TimePicker {...timePickerProps} />;
  };
  const {
    name,
    required,
    category,
    dynamicFieldName,
    disabled,
    hideLabel,
    disabledHours,
    disabledMinutes,
    defaultValue,
    format,
    formRules,
    onOpenChange,
  } = props;
  const intl = useIntl();
  const label = hideLabel
    ? null
    : getFormItemLabel(intl, category, name, dynamicFieldName);
  const arr: string[] = [];
  if (required) {
    arr.push("required");
  }
  const rules = getFormItemVaidation(intl, label, arr);
  const dateFormat = format ? format : "HH:mm";
  const restricted = useContext(RestrictedContext);
  return (
    <Form.Item
      label={hideLabel ? null : label}
      name={name}
      rules={formRules ? [...formRules, rules] : rules}
    >
      <HourPickerComponent
        disabled={disabled || !restricted["hasWritePermission"]}
        disabledHours={disabledHours}
        disabledMinutes={disabledMinutes}
        defaultValue={defaultValue}
        onOpenChange={onOpenChange}
        onChange={() => {}}
      />
    </Form.Item>
  );
};

interface RangePickerItemProps {
  name: string | string[];
  dynamicFieldName?: string;
  required?: boolean;
  category?: string;
  disabled?: boolean;
  hideLabel?: boolean;
  disabledHours?: () => number[];
  disabledMinutes?: (selectedHour: number) => number[];
  defaultValue?: [Moment, Moment];
  format?: string;
  formRules?: any[];
}

const RangePickerItem: React.FC<RangePickerItemProps> = (props) => {
  const RangePickerComponent: React.FC<{
    value?: [Moment | null, Moment | null] | null;
    onChange: (value: [Moment | null, Moment | null] | null) => void;
    disabled?: boolean;
    disabledHours?: () => number[];
    disabledMinutes?: (selectedHour: number) => number[];
    defaultValue?: [Moment, Moment];
  }> = ({
    value,
    onChange,
    disabled,
    disabledHours,
    disabledMinutes,
    defaultValue,
  }) => {
    const onDateChange = (val: [Moment | null, Moment | null] | null) => {
      onChange(val);
    };
    const restricted = useContext(RestrictedContext);
    return (
      <RangePicker
        disabled={disabled || !restricted["hasWritePermission"]}
        value={value}
        format={dateFormat}
        style={{ width: "100%" }}
        onChange={onDateChange}
        disabledHours={disabledHours}
        disabledMinutes={disabledMinutes}
        defaultValue={defaultValue}
        minuteStep={15}
      />
    );
  };
  const {
    name,
    required,
    category,
    dynamicFieldName,
    disabled,
    hideLabel,
    disabledHours,
    disabledMinutes,
    defaultValue,
    format,
    formRules,
  } = props;
  const intl = useIntl();
  const label = hideLabel
    ? null
    : getFormItemLabel(intl, category, name, dynamicFieldName);
  const arr: string[] = [];
  if (required) {
    arr.push("required");
  }
  const rules = getFormItemVaidation(intl, label, arr);
  const dateFormat = format ? format : "HH:mm";
  const restricted = useContext(RestrictedContext);
  return (
    <Form.Item
      label={hideLabel ? null : label}
      name={name}
      rules={formRules ? [...formRules, rules] : rules}
    >
      <RangePickerComponent
        disabled={disabled || !restricted["hasWritePermission"]}
        disabledHours={disabledHours}
        disabledMinutes={disabledMinutes}
        defaultValue={defaultValue}
        onChange={() => {}}
      />
    </Form.Item>
  );
};

interface CheckboxItemProps {
  name: string;
  required?: boolean;
  category?: string;
  languages?: boolean;
  days?: boolean;
}

const CheckboxItem: React.FC<CheckboxItemProps> = (props) => {
  const { name, required, category, languages, days } = props;
  const intl = useIntl();
  const label = getFormItemLabel(intl, category, name, null);
  const arr: string[] = [];
  if (required) {
    arr.push("required");
  }
  const rules = getFormItemVaidation(intl, label, arr);

  const languageslData = [
    { value: 1, name: <FormattedMessage id="app.forms.hebrew" /> },
    { value: 2, name: <FormattedMessage id="app.forms.english" /> },
    { value: 3, name: <FormattedMessage id="app.forms.russian" /> },
    { value: 4, name: <FormattedMessage id="app.forms.arabic" /> },
    { value: 5, name: <FormattedMessage id="app.forms.amharic" /> },
    { value: 6, name: <FormattedMessage id="app.forms.spanish" /> },
  ];

  const daysData = [
    { value: "1", name: <FormattedMessage id="app.forms.sunday" /> },
    { value: "2", name: <FormattedMessage id="app.forms.monday" /> },
    { value: "3", name: <FormattedMessage id="app.forms.tuesday" /> },
    { value: "4", name: <FormattedMessage id="app.forms.wednesday" /> },
    { value: "5", name: <FormattedMessage id="app.forms.thursday" /> },
    { value: "6", name: <FormattedMessage id="app.forms.friday" /> },
    { value: "7", name: <FormattedMessage id="app.forms.satuarday" /> },
  ];

  let selectData: { value: any; name: React.ReactNode }[] = [];
  if (languages) {
    selectData = languageslData;
  } else if (days) {
    selectData = daysData;
  }
  const restricted = useContext(RestrictedContext);

  return (
    <Form.Item label={label} name={name} rules={rules}>
      <Checkbox.Group>
        {selectData.map((option) => (
          <Row key={option.value} style={{ paddingBottom: 10 }}>
            <Checkbox
              key={option.value}
              value={option.value}
              disabled={!restricted["hasWritePermission"]}
            >
              {option.name}
            </Checkbox>
          </Row>
        ))}
      </Checkbox.Group>
    </Form.Item>
  );
};

interface DymnamicItemFieldsProps {
  record: {
    name: string;
    type: "input" | "date" | "select";
    data?: { value: any; name: React.ReactNode }[];
    yesNo?: boolean;
    marital?: boolean;
    gender?: boolean;
    employeeType?: boolean;
    religion?: boolean;
  }[];
  required?: boolean;
  category?: string;
  name: string;
  addText: React.ReactNode;
  fieldTitle: string;
}

const DymnamicItemFields: React.FC<DymnamicItemFieldsProps> = (props) => {
  const { record, required, category, name, addText, fieldTitle } = props;
  const intl = useIntl();
  return (
    <Form.List name={name}>
      {(fields, { add, remove }) => {
        return (
          <div>
            <Form.Item style={{ justifyContent: "center", width: "100%" }}>
              <Button
                style={{ width: "100%" }}
                type="dashed"
                onClick={() => {
                  add();
                }}
              >
                <PlusOutlined />
                {addText}
              </Button>
            </Form.Item>
            <Collapse bordered={false} accordion>
              {fields.map((field, index) => (
                <Panel
                  header={fieldTitle + " " + (index + 1).toString()}
                  key={index}
                >
                  {record.map((item) =>
                    item.type === "input" ? (
                      <InputItem
                        key={item.name}
                        name={[index.toString(), item.name]}
                        dynamicFieldName={item.name}
                        category={category}
                        required={required}
                      />
                    ) : item.type === "date" ? (
                      <DatePickerItem
                        key={item.name}
                        name={[index.toString(), item.name]}
                        dynamicFieldName={item.name}
                        category={category}
                        required={required}
                      />
                    ) : item.type === "select" ? (
                      <SelectItem
                        key={item.name}
                        name={[index.toString(), item.name]}
                        dynamicFieldName={item.name}
                        category={category}
                        required={required}
                        data={item.data}
                        yesNo={item.yesNo}
                        marital={item.marital}
                        gender={item.gender}
                        employeeType={item.employeeType}
                        religion={item.religion}
                      />
                    ) : null
                  )}
                  <Button danger onClick={() => remove(field.name)}>
                    {intl.formatMessage({
                      id: "app.actions.delete",
                    })}
                  </Button>
                </Panel>
              ))}
            </Collapse>
          </div>
        );
      }}
    </Form.List>
  );
};

export {
  InputItem,
  TextAreaItem,
  SliderItem,
  SelectItem,
  DatePickerItem,
  HourPickerItem,
  RangePickerItem,
  CheckboxItem,
  DymnamicItemFields,
};
